import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concat, defer, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { IResponseBadRequest } from '@core/interfaces/response-bad-request';
import { CreditCardVaultService, I18nService, PayService } from '@core/services';
import { ComponentEnum } from '@core/enums';
import { getFirstVaultedCard, requireVaultedCardsClientId } from '@/features/checkout/domain/util';
import { CheckoutOrderProcessor } from '@/features/checkout/domain/processor';
import { NotificationEnum } from '@shared/components/notification/enum/notification.enum';
import {
	CHECKOUT_UPDATE_CREDIT_CARD_FORM,
	CREATE_TOKEN_ASSOCIATED_CARD,
	CREATE_TOKEN_ASSOCIATED_CARD_FAILURE,
	CREATE_TOKEN_ASSOCIATED_CARD_SUCCESS,
	CREATE_VAULTED_CARD,
	CREATE_VAULTED_CARD_FAILURE,
	CREATE_VAULTED_CARD_SUCCESS,
	DELETE_VAULTED_CARD,
	DELETE_VAULTED_CARD_FAILURE,
	DELETE_VAULTED_CARD_SUCCESS,
	FIND_VAULTED_CARD_BY_ID,
	FIND_VAULTED_CARD_BY_ID_FAILURE,
	FIND_VAULTED_CARD_BY_ID_SUCCESS,
	FIND_VAULTED_CARDS,
	FIND_VAULTED_CARDS_FAILURE,
	FIND_VAULTED_CARDS_SUCCESS,
	NOTIFICATION_SHOW,
	SET_DEFAULT_VAULTED_CARD,
	SET_DEFAULT_VAULTED_CARD_FAILURE,
	SET_DEFAULT_VAULTED_CARD_SUCCESS,
	VAULTED_CARDS_UPDATE_LOADING,
} from '@store/actions';
import { selectCustomerMerchId, selectClient, selectLanguaje } from '@store/selectors';

@Injectable()
export class CreditCardVaultEffects {
	constructor(
		private actions$: Actions,
		private vaultService: CreditCardVaultService,
		private store: Store,
		private payService: PayService,
		private checkoutOrderProcessor: CheckoutOrderProcessor,
		private i18nService: I18nService,
		private creditCardVaultService: CreditCardVaultService,
	) { }

	loadVaultedCards$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FIND_VAULTED_CARDS),
			mergeMap(({ limit, offset, clientId }) =>
				defer(() => {
					const validatedClientId = requireVaultedCardsClientId(clientId);
					return concat(
						of(VAULTED_CARDS_UPDATE_LOADING({ loading: true })),
						this.vaultService.getAllVaultedCards(limit, offset, validatedClientId).pipe(
							mergeMap(response =>
								([FIND_VAULTED_CARDS_SUCCESS({ response }), VAULTED_CARDS_UPDATE_LOADING({ loading: false })])
							),
							catchError(error =>
								from([
									VAULTED_CARDS_UPDATE_LOADING({ loading: false }),
									FIND_VAULTED_CARDS_FAILURE({
										error: {
											...error,
											message: error?.message || 'Failed to load vaulted cards',
										} as IResponseBadRequest,
									}),
								])
							)
						)
					);
				}).pipe(
					catchError(error =>
						of(
							FIND_VAULTED_CARDS_FAILURE({
								error: {
									...error,
									message: error?.message || 'Failed to load vaulted cards',
								} as IResponseBadRequest,
							})
						)
					)
				)
			)
		)
	);

	createTokenAssociatedCard$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CREATE_TOKEN_ASSOCIATED_CARD),
			mergeMap(action =>
				this.payService.getToken().pipe(
					switchMap(({ token }) =>
						concat(
							of(VAULTED_CARDS_UPDATE_LOADING({ loading: true })),
							defer(() =>
								this.store.select(selectCustomerMerchId).pipe(
									take(1),
									switchMap(customerMerchId =>
										this.checkoutOrderProcessor
											.createAssociatedCard(
												{
													customerData: action.customerData,
													isLoggedIn: action.isLoggedIn,
													customerMerchId,
													token,
												},
												{
													createGuest: (cardToken, customerData) =>
														this.vaultService.createTokenAssociatedCardGuest(cardToken, customerData),
													createLoggedIn: (cardToken, customerData, merchantCustomerId) =>
														this.vaultService.createTokenAssociatedCardLogged(
															cardToken,
															customerData,
															merchantCustomerId
														),
												}
											)
											.pipe(
												mergeMap(response => {
													const actions = [
														CREATE_TOKEN_ASSOCIATED_CARD_SUCCESS({
															response: response.normalizedResponse,
															cardSummary: action.cardSummary,
														}),
														...(response.vaultedCardRequest
															? [
																CREATE_VAULTED_CARD({
																	request: response.vaultedCardRequest,
																}),
															]
															: []),
														CHECKOUT_UPDATE_CREDIT_CARD_FORM({ formData: null }),
														VAULTED_CARDS_UPDATE_LOADING({ loading: false }),
													];
													return from(actions);
												})
											)
									)
								)
							).pipe(
								catchError(error => {
									const details = Array.isArray(error?.details)
										? error.details.join(', ')
										: (Array.isArray(error?.error?.details)
											? error.error.details.join(', ')
											: null);
									const resolvedMessage = details || error?.message || error?.error?.message || 'Failed to associate card';

									return from([
										CREATE_TOKEN_ASSOCIATED_CARD_FAILURE({
											error: {
												...error,
												message: resolvedMessage,
											} as IResponseBadRequest,
										}),
										VAULTED_CARDS_UPDATE_LOADING({ loading: false }),
									]);
								})
							)
						)
					),
					catchError(error =>
						of(
							CREATE_TOKEN_ASSOCIATED_CARD_FAILURE({
								error: {
									...error,
									message: error?.message || 'Failed to retrieve payment token',
								} as IResponseBadRequest,
							}),
						)
					)
				)
			)
		)
	);

	notifyCreateTokenAssociatedCardSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CREATE_TOKEN_ASSOCIATED_CARD_SUCCESS),
			filter(({ cardSummary }) => !!cardSummary),
			map(() =>
				NOTIFICATION_SHOW({
					notification: {
						type: NotificationEnum.Success,
						title: 'Card added',
						detail: 'Card added successfully',
					},
				})
			),
			tap(() => {
				this.creditCardVaultService.closeForm();
			})
		)
	);

	handleCreateTokenAssociatedCardFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CREATE_TOKEN_ASSOCIATED_CARD_FAILURE),
			map(({ error }) => {
				const detail = error?.message || 'Failed to associate card';

				return NOTIFICATION_SHOW({
					notification: {
						type: NotificationEnum.Error,
						title: 'Error',
						detail,
					},
				});
			})
		)
	);

	loadVaultedCardById$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FIND_VAULTED_CARD_BY_ID),
			mergeMap(({ idVaultedCard, isUserAction }) => {
				if (!idVaultedCard) {
					return of(FIND_VAULTED_CARD_BY_ID_SUCCESS({ card: null, isUserAction }));
				}
				this.store.dispatch(VAULTED_CARDS_UPDATE_LOADING({ loading: true }));

				return this.vaultService.getVaultedCardById(idVaultedCard).pipe(
					mergeMap(response => {

						return [
							FIND_VAULTED_CARD_BY_ID_SUCCESS({
								card: getFirstVaultedCard(response),
								isUserAction
							}),
							VAULTED_CARDS_UPDATE_LOADING({ loading: false })
						];
					}),
					catchError(error =>
						of(
							FIND_VAULTED_CARD_BY_ID_FAILURE({
								error: {
									...error,
									message: error?.message || 'Failed to load vaulted card',
								} as IResponseBadRequest,
							}),
							NOTIFICATION_SHOW({
								notification: {
									type: 'error',
									title: 'Operation Failed',
									detail: 'An error occurred',
								},
							}),
							VAULTED_CARDS_UPDATE_LOADING({ loading: false })
						)
					)
				);
			})
		)
	);

	notifyVaultedCardSelectedByUser$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FIND_VAULTED_CARD_BY_ID_SUCCESS),
			filter(({ card, isUserAction }) => isUserAction === true && !!card),
			withLatestFrom(this.store.select(selectLanguaje)),
			map(([{ card }, lang]) => {
				const translations = this.i18nService.getTranslations(ComponentEnum.CreditCardForm, lang);

				return NOTIFICATION_SHOW({
					notification: {
						type: NotificationEnum.Success,
						title: translations['cardSelected'],
						detail: `${translations['cardSelectedDetail']}${card?.lastDigits ?? ''}`,
					},
				});
			})
		)
	);

	notifyDeleteVaultedCardSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DELETE_VAULTED_CARD_SUCCESS),
			withLatestFrom(this.store.select(selectLanguaje)),
			map(([, lang]) => {
				const translations = this.i18nService.getTranslations(ComponentEnum.CreditCardForm, lang);

				return NOTIFICATION_SHOW({
					notification: {
						type: NotificationEnum.Success,
						title: translations['cardDeleted'],
						detail: translations['cardDeletedDetail'],
					},
				});
			})
		)
	);

	notifySetDefaultVaultedCardSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SET_DEFAULT_VAULTED_CARD_SUCCESS),
			withLatestFrom(this.store.select(selectLanguaje)),
			map(([, lang]) => {
				const translations = this.i18nService.getTranslations(ComponentEnum.CreditCardForm, lang);

				return NOTIFICATION_SHOW({
					notification: {
						type: NotificationEnum.Success,
						title: translations['defaultCard'],
						detail: translations['defaultCardUpdated'],
					},
				});
			})
		)
	);

	notifyVaultedCardFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				DELETE_VAULTED_CARD_FAILURE,
				SET_DEFAULT_VAULTED_CARD_FAILURE,
				FIND_VAULTED_CARD_BY_ID_FAILURE
			),
			withLatestFrom(this.store.select(selectLanguaje)),
			map(([, lang]) => {
				const translations = this.i18nService.getTranslations(ComponentEnum.CreditCardForm, lang);

				return NOTIFICATION_SHOW({
					notification: {
						type: NotificationEnum.Error,
						title: translations['operationFailed'],
						detail: translations['anErrorOccurred'],
					},
				});
			})
		)
	);

	createVaultedCard$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CREATE_VAULTED_CARD),
			mergeMap(({ request }) => {
				this.store.dispatch(VAULTED_CARDS_UPDATE_LOADING({ loading: true }));

				return this.vaultService.createVaultedCard(request).pipe(
					map(response =>
						CREATE_VAULTED_CARD_SUCCESS({
							card: this.checkoutOrderProcessor.processVaultedCardCreationResponse(response),
						}),
						VAULTED_CARDS_UPDATE_LOADING({ loading: false }),
					),
					catchError(error => {
						const errorMessage = error?.error?.message || error?.message || 'This card is already registered';
						return from([
							CREATE_VAULTED_CARD_FAILURE({
								error: {
									...error,
									message: errorMessage,
								} as IResponseBadRequest,
							}),
							NOTIFICATION_SHOW({
								notification: {
									type: NotificationEnum.Error,
									title: NotificationEnum.Error,
									detail: errorMessage,
								},
							}),
							VAULTED_CARDS_UPDATE_LOADING({ loading: false }),
						]);
					})
				)
			}
			)
		)
	);

	refreshVaultedCardsOnCreateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CREATE_VAULTED_CARD_SUCCESS),
			withLatestFrom(this.store.select(selectClient)),
			filter(([_, client]) => !!client?.id),
			map(([_, client]) =>
				FIND_VAULTED_CARDS({
					limit: 10,
					offset: 0,
					clientId: client!.id,
				})
			)
		)
	);

	deleteVaultedCard$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DELETE_VAULTED_CARD),
			mergeMap(({ id }) => {
				this.store.dispatch(VAULTED_CARDS_UPDATE_LOADING({ loading: true }));
				return this.vaultService.deleteVaultedCard(id).pipe(
					mergeMap(() => {

						return [
							DELETE_VAULTED_CARD_SUCCESS({ id }),
							NOTIFICATION_SHOW({
								notification: {
									type: 'success',
									title: 'Card deleted',
									detail: 'Card Deleted Success',
								},
							}),
							VAULTED_CARDS_UPDATE_LOADING({ loading: false })
						]
					}),
					catchError(error =>
						of(
							DELETE_VAULTED_CARD_FAILURE({
								error: {
									...error,
									message: error?.message || 'Failed to delete vaulted card',
								} as IResponseBadRequest,
							}),
							NOTIFICATION_SHOW({
								notification: {
									type: 'error',
									title: 'Operation Failed',
									detail: 'An error occurred',
								},
							}),
							VAULTED_CARDS_UPDATE_LOADING({ loading: false })
						)
					)
				)
			}
			)
		)
	);

	setDefaultVaultedCard$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SET_DEFAULT_VAULTED_CARD),
			mergeMap(({ idVaultedCard, isDefault }) => {
				this.store.dispatch(VAULTED_CARDS_UPDATE_LOADING({ loading: true }));

				return this.vaultService.setDefaultVaultedCard(idVaultedCard, isDefault).pipe(
					mergeMap(() => {
						return [
							SET_DEFAULT_VAULTED_CARD_SUCCESS({
								idVaultedCard,
								isDefault,
							}),
							NOTIFICATION_SHOW({
								notification: {
									type: 'success',
									title: 'Default Card',
									detail: 'Default Card Updated',
								},
							}),
							VAULTED_CARDS_UPDATE_LOADING({ loading: false })
						]
					}),
					catchError(error =>
						of(
							SET_DEFAULT_VAULTED_CARD_FAILURE({
								error: {
									...error,
									message: error?.message || 'Failed to update default vaulted card',
								} as IResponseBadRequest,
							}),
							NOTIFICATION_SHOW({
								notification: {
									type: 'error',
									title: 'Operation Failed',
									detail: 'An error occurred',
								},
							}),
							VAULTED_CARDS_UPDATE_LOADING({ loading: false })
						)
					)
				)
			})
		)
	);
}
